import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, BackdropCircular } from 'components';
import { format } from 'date-fns';
import {
  billCustomerSubscriptionApi,
  editCustomerSubscriptionApi,
  getCustomerSubscriptionBillingInfoApi
} from 'api';
import {
  Button,
  Tab,
  Tabs,
} from '@material-ui/core';
import { useStyles } from './styles';
import DetailsForm from './Details/DetailsForm';
import HistoryList from './History/HistoryList';
import PaymentInfo from './Payment/PaymentInfo';
import ShippingForm from './Shipping/ShippingForm';


const ShippingNotification = () => (
  <div className="h-full-screen">
    <p className="m-5 text-muted">
      {'This subscription is virtual, so there\'s no need to set a shipping address.'}
    </p>
  </div>
);


const EditSubscription = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const {id, incrementId} = useParams<{ id: string, incrementId: string }>();
  const [tabValue, setTabValue] = useState('details');
  const [isVirtualSubscription, setVirtualSubscription] = useState(false);
  const {
    customerSubscriptionBillingInfo,
    isCustomerSubscriptionsLoading,
    isSubscriptionBillingInfoLoading,
    isCustomerSubscriptionShippingLoading,
    isCustomerSubscriptionHistoryLoading,
    isLoadingSubscriptionStorePickup,
    editableCustomerSubscription,
    isSubscriptionBilling
  } = useSelector((state: IReduxState) => state.customer_subscriptions);
  const { loading } = useSelector((state: IReduxState) => state.store_configs);

  const { extension_attributes } = editableCustomerSubscription || {};

  const locationState = history.location.state as LocationState;
  const breadcrumbPath = locationState && locationState.params ? `/subscriptions${locationState.params}` : '/subscriptions';

  const productsSkus = extension_attributes?.products.map((p) => {
    return p.replaceAll(')', '').split('SKU: ')[1];
  });

  useEffect(() => {
    const isAllVirtual = productsSkus?.every((sku) => !sku.includes('PROD'));
    setVirtualSubscription(isAllVirtual);
  }, [productsSkus]);

  useEffect(() => {
    getCustomerSubscriptionBillingInfoApi({dispatch, id});
  }, []);

  const handleChangeSubscriptionStatus = (status) => {
    const subscription = {...editableCustomerSubscription, status};
    editCustomerSubscriptionApi({dispatch, subscription });
  };

  const handleChangeNextRun = () => {
    const prevNextRun = new Date(editableCustomerSubscription.next_run);

    if (editableCustomerSubscription.frequency_unit === 'month') {
      prevNextRun.setMonth(prevNextRun.getMonth() + editableCustomerSubscription.frequency_count);
    } else if (editableCustomerSubscription.frequency_unit === 'week') {
      prevNextRun.setDate(prevNextRun.getDate() + (editableCustomerSubscription.frequency_count * 7));
    }

    const subscription = {...editableCustomerSubscription, next_run: format(prevNextRun, 'yyyy-MM-dd 00:00:00')};
    editCustomerSubscriptionApi({dispatch, subscription });
  };

  const isLoading = isCustomerSubscriptionsLoading
  || isSubscriptionBillingInfoLoading
  || isCustomerSubscriptionShippingLoading
  || isCustomerSubscriptionHistoryLoading
  || isLoadingSubscriptionStorePickup
  || isSubscriptionBilling
  || loading;

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Subscriptions', path: breadcrumbPath},
            { name: incrementId}
          ]}
        />
      </div>
      <div className={`${classes.card} px-5`}>
        <div className={`${classes.tabsContainer} ${classes.tabIndicator} pt-5 px-5 flex justify-between w-full`}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
          >
            <Tab
              label="Details"
              value="details"
              className={tabValue === 'details' ? classes.activeTab : classes.inactiveTab}
            />
            <Tab
              label="Payment"
              value="payment"
              className={tabValue === 'payment' ? classes.activeTab : classes.inactiveTab}
            />
            <Tab
              label="Shipping"
              value="shipping"
              className={tabValue === 'shipping' ? classes.activeTab : classes.inactiveTab}
            />
            <Tab
              label="History"
              value="history"
              className={tabValue === 'history' ? classes.activeTab : classes.inactiveTab}
            />
          </Tabs>
          <div>
            {editableCustomerSubscription.status === 'active' &&
            <>
              <Button
                className={classes.actionButton}
                variant="outlined"
                onClick={handleChangeNextRun}
              >
                Skip
              </Button>
              <Button
                className={classes.actionButton}
                variant="outlined"
                onClick={() => handleChangeSubscriptionStatus('paused')}
              >
                Pause
              </Button>
              <Button
                className={classes.actionButton}
                variant="outlined"
                onClick={() => billCustomerSubscriptionApi({dispatch, id})}
              >
                Bill Now
              </Button>
            </>
            }
            {['canceled', 'payment_failed', 'paused'].includes(editableCustomerSubscription.status) &&
              <Button
                className={classes.actionButton}
                variant="outlined"
                onClick={() => handleChangeSubscriptionStatus('active')}
              >
                Reactivate
              </Button>
            }
            {['active', 'payment_failed', 'paused'].includes(editableCustomerSubscription.status) &&
              <Button
                className={classes.actionButton}
                variant="outlined"
                onClick={() => handleChangeSubscriptionStatus('canceled')}
              >
                Cancel
              </Button>
            }
          </div>
        </div>
        <div>
          {tabValue === 'details' && <DetailsForm goBackPath={breadcrumbPath} />}
          {tabValue === 'payment' && <PaymentInfo goBackPath={breadcrumbPath} customerSubscriptionBillingInfo={customerSubscriptionBillingInfo} />}
          {tabValue === 'shipping' &&
            <>
              {isVirtualSubscription ? <ShippingNotification /> : <ShippingForm goBackPath={breadcrumbPath} />}
            </>
          }
          {tabValue === 'history' && <HistoryList />}
        </div>
      </div>
      <BackdropCircular loading={isLoading} />
    </div>
  );
};

export default EditSubscription;
